import { AlertTriangle, ChevronDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { DataConflict } from "@shared/discovery-types";
import { cn } from "@/lib/utils";

interface ConflictIndicatorProps {
  conflict: DataConflict;
  onResolve: (selectedIndex: number) => void;
  className?: string;
}

export function ConflictIndicator({
  conflict,
  onResolve,
  className,
}: ConflictIndicatorProps) {
  if (conflict.resolved) {
    return (
      <span
        className={cn(
          "text-xs text-muted-foreground flex items-center gap-1",
          className
        )}
      >
        <Check className="h-3 w-3 text-success" />
        Resolved
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-6 px-2 text-xs text-warning hover:text-warning hover:bg-warning/10",
            className
          )}
        >
          <AlertTriangle className="h-3 w-3 mr-1" />
          {conflict.values.length} alternatives
          <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          Select value for {conflict.fieldLabel}
        </div>
        {conflict.values.map((value, index) => (
          <DropdownMenuItem
            key={`${value.source}-${index}`}
            onClick={() => onResolve(index)}
            className={cn(
              "flex flex-col items-start gap-0.5 cursor-pointer",
              index === conflict.selectedIndex && "bg-accent"
            )}
          >
            <span className="font-medium">{value.value}</span>
            <span className="text-xs text-muted-foreground">
              from {value.source} ({value.confidence}% confidence)
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
